import * as THREE from 'three';
import nipplejs from 'nipplejs';
import { BaseInputController } from './BaseInputController';
import { GameScene } from '../scenes/GameScene';

export class DPadInputController extends BaseInputController {
  private joystick: nipplejs.JoystickManager | null = null;
  private joystickContainer: HTMLElement | null = null;
  private isJoystickActive: boolean = false;
  private joystickDirection: THREE.Vector2 = new THREE.Vector2();
  private joystickInterval: NodeJS.Timeout | null = null;
  private shootButton: HTMLButtonElement | null = null;
  private passButton: HTMLButtonElement | null = null;

  constructor(camera: THREE.PerspectiveCamera, gameScene: GameScene) {
    super(camera, gameScene);
    this.createJoystick();
    this.createActionButtons();
  }

  private createJoystick(): void {
    // Create a container for the joystick
    this.joystickContainer = document.createElement('div');
    this.joystickContainer.id = 'joystick-container';
    this.joystickContainer.style.position = 'absolute';
    this.joystickContainer.style.bottom = '20px';
    this.joystickContainer.style.left = '20px';
    this.joystickContainer.style.width = '150px';
    this.joystickContainer.style.height = '150px';
    this.joystickContainer.style.backgroundColor = 'rgba(0, 0, 0, 0.3)';
    this.joystickContainer.style.borderRadius = '50%';
    this.joystickContainer.style.border = '2px solid rgba(255, 255, 255, 0.5)';
    document.body.appendChild(this.joystickContainer);
    
    // Initialize the joystick
    this.joystick = nipplejs.create({
      zone: this.joystickContainer,
      mode: 'static',
      position: { left: '50%', top: '50%' },
      color: 'rgba(255, 255, 255, 0.8)',
      size: 120,
      lockX: false,
      lockY: false
    });

    this.joystick.on('start', () => {
      this.isJoystickActive = true;
      this.joystickInterval = setInterval(() => {
        if (this.isJoystickActive) {
          const playerPosition = this.gameScene.getPlayerPosition();
          if (playerPosition) {
            // Use a base move speed that will be adjusted by deltaTime in update()
            const baseSpeed = 6;
            // Store normalized direction - actual velocity will be calculated in update()
            this.joystickDirection.set(
              this.joystickDirection.x * baseSpeed,
              -this.joystickDirection.y * baseSpeed
            );
          }
        }
      }, 10);
    });

    this.joystick.on('end', () => {
      this.isJoystickActive = false;
      if (this.joystickInterval) {
        clearInterval(this.joystickInterval);
      }
      this.gameScene.movePlayer(0, 0);
    });
    
    this.joystick.on('move', (evt, data) => {
      if (this.isJoystickActive && data && data.vector) {
        this.joystickDirection.set(data.vector.x, data.vector.y);        
      }
    });
  }

  private createActionButtons(): void {
    // Create shoot/jump button
    this.shootButton = document.createElement('button');
    this.shootButton.textContent = 'Shoot/Jump';
    this.shootButton.style.position = 'absolute';
    this.shootButton.style.bottom = '60px';
    this.shootButton.style.right = '20px';
    this.shootButton.style.zIndex = '1000';
    this.shootButton.style.padding = '12px';
    this.shootButton.style.width = '120px';
    this.shootButton.style.height = '120px';
    this.shootButton.style.backgroundColor = 'rgba(51, 51, 51, 0.7)';
    this.shootButton.style.color = 'white';
    this.shootButton.style.border = 'none';
    this.shootButton.style.borderRadius = '60px';
    this.shootButton.style.cursor = 'pointer';
    this.shootButton.style.fontSize = '16px';
    this.shootButton.style.fontWeight = 'bold';
    this.shootButton.style.userSelect = 'none';
    this.shootButton.style.touchAction = 'manipulation';
    this.shootButton.style.display = 'flex';
    this.shootButton.style.alignItems = 'center';
    this.shootButton.style.justifyContent = 'center';

    this.shootButton.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      if (this.gameScene.playerHasBall()) {
        this.gameScene.processPlayerShoot();
      } else {
        this.gameScene.processPlayerBlock();
      }
    }, { passive: false });

    document.body.appendChild(this.shootButton);

    // Create pass button
    this.passButton = document.createElement('button');
    this.passButton.textContent = 'Pass';
    this.passButton.style.position = 'absolute';
    this.passButton.style.bottom = '200px';
    this.passButton.style.right = '20px';
    this.passButton.style.zIndex = '1000';
    this.passButton.style.padding = '12px';
    this.passButton.style.width = '120px';
    this.passButton.style.height = '120px';
    this.passButton.style.backgroundColor = 'rgba(51, 51, 51, 0.7)';
    this.passButton.style.color = 'white';
    this.passButton.style.border = 'none';
    this.passButton.style.borderRadius = '60px';
    this.passButton.style.cursor = 'pointer';
    this.passButton.style.fontSize = '16px';
    this.passButton.style.fontWeight = 'bold';
    this.passButton.style.userSelect = 'none';
    this.passButton.style.touchAction = 'manipulation';
    this.passButton.style.display = 'flex';
    this.passButton.style.alignItems = 'center';
    this.passButton.style.justifyContent = 'center';
    this.passButton.style.opacity = '0.5';
    this.passButton.style.pointerEvents = 'none';

    this.passButton.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      if (this.gameScene.playerHasBall()) {
        const playerPos = this.gameScene.getPlayerPosition();
        if (playerPos) {
          const teammatePos = this.gameScene.getClosestTeammatePosition(playerPos);
          if (teammatePos) {
            const direction = new THREE.Vector3()
              .subVectors(teammatePos, playerPos)
              .normalize();
            this.gameScene.processPlayerPass(direction.x, direction.z);
          }
        }
      }
    }, { passive: false });

    document.body.appendChild(this.passButton);

    // Update pass button state
    setInterval(() => {
      if (this.gameScene.playerHasBall()) {
        const playerPos = this.gameScene.getPlayerPosition();
        if (playerPos) {
          const teammatePos = this.gameScene.getClosestTeammatePosition(playerPos);
          if (teammatePos) {
            this.passButton.style.opacity = '1';
            this.passButton.style.pointerEvents = 'auto';
          } else {
            this.passButton.style.opacity = '0.5';
            this.passButton.style.pointerEvents = 'none';
          }
        }
      } else {
        this.passButton.style.opacity = '0.5';
        this.passButton.style.pointerEvents = 'none';
      }
    }, 1000);
  }

  public update(deltaTime: number): void {
    // Apply frame-rate independent movement in the joystick system
    if (this.isJoystickActive) {
      const playerPosition = this.gameScene.getPlayerPosition();
      if (playerPosition) {
        // Apply deltaTime for frame-rate independent movement
        const velocityX = this.joystickDirection.x * (deltaTime / (1/60)); // Normalized to 60 FPS
        const velocityZ = this.joystickDirection.y * (deltaTime / (1/60)); // Y is already inverted
        this.gameScene.movePlayer(velocityX, velocityZ);
      }
    }
  }

  public dispose(): void {
    if (this.joystick) {
      this.joystick.destroy();
    }
    
    if (this.joystickContainer && this.joystickContainer.parentElement) {
      this.joystickContainer.parentElement.removeChild(this.joystickContainer);
    }

    if (this.shootButton && this.shootButton.parentElement) {
      this.shootButton.parentElement.removeChild(this.shootButton);
    }
    if (this.passButton && this.passButton.parentElement) {
      this.passButton.parentElement.removeChild(this.passButton);
    }
  }
} 